import React from 'react';

import { EditableDate } from './EditableDate';
import { EditableSelect } from './EditableSelect';
import { JobDetailsPanelProps } from './JobDetailsPanel.props';
import { FieldGrid, FieldItem } from '../../../../../components/atoms/FieldGrid';
import { Panel } from '../../../../../components/atoms/Panel';
import { PriorityDot } from '../../../../../components/atoms/PriorityDot';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';
import { displayNodeTypeName } from '../../../../../utils/node-type';
import { findNodeByType } from '../LocationPanel/HierarchyTree';

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } catch {
    return '—';
  }
};

export const JobDetailsPanel: React.FC<JobDetailsPanelProps> = ({
  service,
  onFieldUpdate,
  actionTypeOptions,
  onActionTypeUpdate,
  isClient = false,
}) => {
  const statusName = service?.serviceStatus?.name ?? '';
  const priorityLabel = service?.priorityLabel ?? service?.priorityCode ?? '';
  const serviceTypeName = service?.serviceType?.[0]?.name ?? service?.serviceTypeCode ?? '—';
  const actionTypeName = service?.actionType?.[0]?.name ?? service?.actionTypeCode ?? '—';
  const createdBy = service?.loggedBy ?? '—';
  // "Outlet type" surfaces the type of the parent outlet node in the location
  // hierarchy. Clients use this to confirm the job sits at the expected venue
  // category (e.g. branch vs kiosk). Falls back to a dash when no outlet
  // ancestor exists or the hierarchy hasn't loaded yet.
  const outletNode = findNodeByType(service?.locationHierarchy ?? null, 'outlet');
  const outletTypeRaw = outletNode?.nodeTypeCode ?? '';
  const outletType = outletTypeRaw ? displayNodeTypeName(outletTypeRaw) : '—';

  return (
    <Panel title="Job Details">
      <FieldGrid columns={2}>
        <FieldItem label="Job Type" value={serviceTypeName} />
        <FieldItem label="Status" value={statusName ? <StatusBadge status={statusName} /> : '—'} />
        <FieldItem
          label="Action Type"
          value={
            onActionTypeUpdate && actionTypeOptions && actionTypeOptions.length > 0 ? (
              <EditableSelect
                label=""
                ariaLabel="Action Type"
                value={service?.actionTypeCode}
                displayValue={actionTypeName}
                options={actionTypeOptions}
                helperText="Changing the action type may affect allocation and SLA matching."
                onSave={onActionTypeUpdate}
              />
            ) : (
              actionTypeName
            )
          }
        />
        <FieldItem
          label="Priority"
          value={priorityLabel ? <PriorityDot priority={priorityLabel} /> : '—'}
        />
        <FieldItem label="Client" value={service?.client ?? '—'} />
        <FieldItem label="Outlet Type" value={outletType} />
        {!isClient && (
          <FieldItem label="HD Operator" value={service?.secondaryAllocation?.node?.name ?? '—'} />
        )}
        <FieldItem
          label="Scheduled Date"
          value={
            <EditableDate
              label=""
              value={service?.scheduledAt}
              displayValue={formatDate(service?.scheduledAt)}
              onSave={onFieldUpdate ? (val) => onFieldUpdate('scheduledAt', val) : undefined}
            />
          }
        />
        <FieldItem label="Created Date" value={formatDateTime(service?.createdAt)} />
        <FieldItem label="Closed At" value={formatDateTime(service?.closedAt)} />
        <FieldItem label="Created By" value={createdBy} />
        <FieldItem
          label="Follow Up"
          value={
            <EditableDate
              label=""
              value={service?.followUpAt}
              type="datetime-local"
              displayValue={formatDateTime(service?.followUpAt)}
              onSave={onFieldUpdate ? (val) => onFieldUpdate('followUpAt', val) : undefined}
            />
          }
        />
        <FieldItem label="Zone" value={service?.zone ?? '—'} />
      </FieldGrid>
    </Panel>
  );
};
